import { useStore } from 'components/Store';
import { useCallback } from 'react';
import axios, { AxiosError, AxiosRequestConfig, CancelToken } from 'axios';
import { CROPWISE_PROXY_URL } from 'utilities/apiConstants';
import { getAccessToken } from 'utilities/base-auth';
import { parseServerError } from 'helpers/errorHelpers';
import { Geometry } from 'geojson';
import {
  SORT_BY_VALUES,
  SORT_ORDER_VALUES,
  OperationSummaryParams,
  OperationSummaryResponse
} from 'components/RecordsListView/types';
import useSelectedCropSeasonIds from 'hooks/useSelectedCropSeasonIds';
import {
  FormValues,
  RecordSource
} from '../../StepsLayout/Constants/formikData';
import {
  createBasePayloadBody,
  getRequestConfig,
  buildOperationSummaryQueryString,
  handleOperationSummaryError,
  Organization
} from '../../apiHelpers';

// Interface for the API response Application structure
export interface ApplicationSummary {
  id: string;
  name: string;
  startDate: string;
  fieldsBrief: {
    firstFields: Array<{
      id: string;
      name: string;
    }>;
    firstFieldInfo: {
      id: string;
      name: string;
      geometry: Geometry;
    };
    totalFields: number;
  };
  recordSource?: {
    recordSourceId: string;
    recordSourceType: string;
    recordSourceAdditionalInfo?: string;
  };
  tagIds?: string[];
  [key: string]: any; // Allow for additional API fields
}

// Interface for the API response
export type ApplicationSummaryResponse = OperationSummaryResponse<
  ApplicationSummary
>;

export interface ApplicationDetails {
  id: string;
  orgId: string;
  name: string;
  type: WorkType;
  startDate: string;
  endDate: string;
  totalCost: number | null | undefined;
  totalAppliedArea: number;
  totalAppliedAreaUnit: string;
  cropSeasonIds: string[];
  authorization: Authorization;
  notes?: string;
  properties: Property[];
  products: Product[];
  tankInformation?: TankInformation;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  timingEvent?: TimingEvent;
  applicationMethod?: ApplicationMethod;
  equipmentIds?: string[];
  applicators?: Applicator[];
  recordSource?: RecordSource;
  reiExpirationDate?: string;
  phiExpirationDate?: string;
  tagIds?: string[];
  weather?: Weather;
}

export interface Authorization {
  authorizerId?: string;
  authorizationDate: string;
}

interface Applicator {
  personId?: string;
  companyId?: string;
}

export interface Property {
  farmId: string;
  fieldId: string;
  cropZoneId: string;
  areaValue: number;
  areaUnit: string;
  areaType: 'REPORTED' | 'BOUNDARY';
  coveragePercent: number;
}

// eslint-disable-next-line no-shadow
export enum ProductQuantityType {
  ratePerAcre = 'RATE_PER_AREA',
  totalProduct = 'TOTAL_PRODUCT',
  ratePerTank = 'RATE_PER_TANK'
}

// eslint-disable-next-line no-shadow
export enum WorkType {
  landPreparation = 'LAND_PREPARATION',
  planting = 'PLANTING',
  irrigation = 'IRRIGATION',
  spraying = 'SPRAYING',
  spreading = 'SPREADING',
  general = 'GENERAL'
}

// eslint-disable-next-line no-shadow
export enum TimingEvent {
  prePlant = 'PRE_PLANT',
  planting = 'PLANTING',
  growing = 'GROWING',
  harvest = 'HARVEST',
  storage = 'STORAGE',
  annual = 'ANNUAL'
}

// eslint-disable-next-line no-shadow
export enum ApplicationMethod {
  aerial = 'AERIAL',
  airBlast = 'AIR_BLAST',
  chemigation = 'CHEMIGATION',
  fertigation = 'FERTIGATION',
  fumigation = 'FUMIGATION',
  ground = 'GROUND',
  groundApplication = 'GROUND_APPLICATION',
  groundBanded = 'GROUND_BANDED',
  groundBroadcast = 'GROUND_BROADCAST',
  groundHooded = 'GROUND_HOODED',
  groundInFurrow = 'GROUND_IN_FURROW',
  groundIncorporated = 'GROUND_INCORPORATED',
  groundSeedTreatment = 'GROUND_SEED_TREATMENT',
  groundSpot = 'GROUND_SPOT',
  groundVariableRate = 'GROUND_VARIABLE_RATE',
  groundWipe = 'GROUND_WIPE',
  planting = 'PLANTING',
  rePlanting = 'RE_PLANTING',
  sidedress = 'SIDEDRESS',
  storage = 'STORAGE',
  topdress = 'TOPDRESS',
  treeInjection = 'TREE_INJECTION',
  waterRun = 'WATER_RUN'
}

export interface ApplicationTargetPest {
  id: string;
  pestNameId: string;
  pestName: string;
}

export interface Product {
  productId: string;
  productName: string;
  productQuantityType: ProductQuantityType;
  priceValue?: number;
  priceUnit?: string;
  totalCostValue?: number;
  totalCostUnit?: string;
  appliedAreaValue: number;
  appliedAreaUnit: string;
  appliedAreaCoveragePercent: number;
  totalProductValue: number;
  totalProductUnit: string;
  ratePerAreaValue: number;
  ratePerAreaUnit: string;
  ratePerTankValue: number;
  ratePerTankUnit: string;
  applicationMethod?: ApplicationMethod;
  targetPestNameIds?: string[];
  registrationNumber?: string;
  custom: boolean;
  isCarrier: boolean;
  isHidden: boolean;
}

export interface FullProduct extends Product {
  productType: string;
  stdUnit: string;
  stdPackageUnit: string;
  manufacturerName: string;
  registrationNumber: string;
  productLabelDocumentId: string | null;
  targetPests?: ApplicationTargetPest[];
}

export interface TankInformation {
  tankSizeValue: number;
  tankSizeUnit: string;
  tankCount: number;
  ratePerAcreValue: number;
  ratePerAcreUnit: string;
  totalProductValue: number;
  totalProductUnit: string;
}

export enum WeatherMode {
  MANUAL = 'MANUAL',
  AUTOMATIC = 'AUTOMATIC'
}

export interface WeatherData {
  skyCondition?: string;
  temperature?: number;
  temperatureUnit?: string;
  humidity?: number;
  windSpeed?: number;
  windSpeedUnit?: string;
  windDirection?: string;
  soilMoisture?: string;
  manuallyModifiedFields?: Array<
    keyof Partial<Omit<WeatherData, 'manuallyModifiedFields'>>
  >;
}
export interface Weather {
  mode: WeatherMode;
  displayAutomaticWeatherModal?: boolean;
  start: WeatherData;
  end: WeatherData;
}

interface ApplicationRequest {
  id?: string;
  orgId: string;
  name: string;
  type: WorkType;
  cropSeasonIds: string[];
  notes?: string;
  startDate: string;
  endDate: string;
  timingEvent?: TimingEvent;
  authorization: Authorization;
  totalCost: number;
  totalAppliedArea: number;
  totalAppliedAreaUnit: string;
  equipmentIds?: string[];
  tagIds?: string[];
  applicators?: Array<Applicator>;
  properties: Array<Property>;
  products: Array<Product>;
  tankInformation?: TankInformation;
  applicationMethod?: ApplicationMethod;
  weather?: Weather;
}

function toPayloadBody(
  organization: Organization,
  formData: FormValues
): ApplicationRequest {
  return createBasePayloadBody(organization, formData, {
    includeEndDate: true,
    includeWeather: true
  }) as ApplicationRequest;
}

const useApplication = () => {
  const {
    state: { organization },
    dispatch
  } = useStore();

  const { selectedCropSeasonIds } = useSelectedCropSeasonIds();
  // Bootstrap dispatches a new `organization` object reference more than once
  // (e.g. SET_ORGANIZATION then SET_USER_ENVIRONMENT) even when the id is
  // unchanged; depending on the id instead of the object keeps this callback
  // — and therefore the summary query it feeds — from rebuilding and
  // re-fetching on every one of those reference changes.
  const organizationId = organization?.id;

  const fetchApplicationList = useCallback(
    async (
      path: string,
      {
        page = 0,
        size = 50,
        fromDate,
        toDate,
        sorts = [
          {
            sortBy: SORT_BY_VALUES.CREATION_DATE,
            order: SORT_ORDER_VALUES.DESC
          },
          { sortBy: SORT_BY_VALUES.NAME, order: SORT_ORDER_VALUES.ASC }
        ],
        filters = {
          name: ''
        },
        id,
        cropZoneIds,
        cropZoneMatchType
      }: Partial<OperationSummaryParams> = {},
      cancelToken?: CancelToken
    ): Promise<ApplicationSummaryResponse> => {
      let response;
      try {
        const queryString = buildOperationSummaryQueryString(
          {
            page,
            size,
            fromDate,
            toDate,
            sorts,
            filters,
            id,
            cropZoneIds,
            cropZoneMatchType
          },
          selectedCropSeasonIds
        );
        const url = `${CROPWISE_PROXY_URL}/v1/base/applications${path}${
          queryString ? `?${queryString}` : ''
        }`;

        response = await axios.get(
          url,
          getRequestConfig(organizationId, cancelToken)
        );
      } catch (error) {
        if (!axios.isCancel(error)) {
          parseServerError(dispatch)(error);
        }

        response = handleOperationSummaryError<ApplicationSummary>(
          error as AxiosError
        );
      }

      return response.data;
    },
    [organizationId, selectedCropSeasonIds, dispatch]
  );

  const getSummaryApplications = useCallback(
    (
      params?: Partial<OperationSummaryParams>,
      cancelToken?: CancelToken
    ): Promise<ApplicationSummaryResponse> =>
      fetchApplicationList('/summary', params, cancelToken),
    [fetchApplicationList]
  );

  const getApplications = useCallback(
    (
      params?: Partial<OperationSummaryParams>,
      cancelToken?: CancelToken
    ): Promise<ApplicationSummaryResponse> =>
      fetchApplicationList('', params, cancelToken),
    [fetchApplicationList]
  );

  const getApplication = useCallback(
    async (applicationId: string, cancelToken?: CancelToken) => {
      const config: AxiosRequestConfig = {
        headers: {
          'Content-Type': 'application/json',
          'cwf-context': JSON.stringify({
            organization_id: organization.id
          }),
          Authorization: `Bearer ${getAccessToken()}`
        },
        cancelToken
      };

      let response;
      try {
        response = await axios.get<ApplicationDetails>(
          `${CROPWISE_PROXY_URL}/v1/base/applications/${applicationId}`,
          config
        );
      } catch (error) {
        if (!axios.isCancel(error)) {
          parseServerError(dispatch)(error);
        }
        throw error;
      }

      return response.data;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [organization]
  );

  const getApplicationProducts = useCallback(
    async (applicationId: string, cancelToken?: CancelToken) => {
      const config: AxiosRequestConfig = {
        headers: {
          'Content-Type': 'application/json',
          'cwf-context': JSON.stringify({
            organization_id: organization.id
          }),
          Authorization: `Bearer ${getAccessToken()}`
        },
        cancelToken
      };

      let response;
      try {
        response = await axios.get<FullProduct[]>(
          `${CROPWISE_PROXY_URL}/v1/base/applications/${applicationId}/products`,
          config
        );
      } catch (error) {
        if (!axios.isCancel(error)) {
          parseServerError(dispatch)(error);
        }
        throw error;
      }
      return response.data;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [organization]
  );

  const createApplication = useCallback(
    async (formData: FormValues) => {
      const body: ApplicationRequest = toPayloadBody(organization, formData);

      const response = await axios.post<ApplicationDetails>(
        `${CROPWISE_PROXY_URL}/v1/base/applications`,
        body,
        {
          headers: {
            'Content-Type': 'application/json',
            'cwf-context': JSON.stringify({
              organization_id: organization.id
            }),
            Authorization: `Bearer ${getAccessToken()}`
          }
        }
      );
      return response.data;
    },
    [organization]
  );

  const editApplication = useCallback(
    async (applicationId: string, formData: FormValues) => {
      const body: ApplicationRequest = toPayloadBody(organization, formData);
      const response = await axios.put<ApplicationDetails>(
        `${CROPWISE_PROXY_URL}/v1/base/applications/${applicationId}`,
        body,
        {
          headers: {
            'Content-Type': 'application/json',
            'cwf-context': JSON.stringify({
              organization_id: organization.id
            }),
            Authorization: `Bearer ${getAccessToken()}`
          }
        }
      );
      return response.data;
    },
    [organization]
  );

  const deleteApplication = useCallback(
    async (applicationId: string) => {
      try {
        const response = await axios.delete(
          `${CROPWISE_PROXY_URL}/v1/base/applications/${applicationId}`,
          {
            headers: {
              'Content-Type': 'application/json',
              'cwf-context': JSON.stringify({
                organization_id: organization.id
              }),
              Authorization: `Bearer ${getAccessToken()}`
            }
          }
        );
        return response.data;
      } catch (error) {
        if (!axios.isCancel(error)) {
          parseServerError(dispatch)(error);
        }
        throw error;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [organization]
  );

  return {
    getSummaryApplications,
    getApplications,
    createApplication,
    editApplication,
    getApplication,
    getApplicationProducts,
    deleteApplication
  };
};

export default useApplication;
